/**
 * Staff actions, from `audit_records`: every on-behalf write, usage-limit
 * change, operator control and account or access change records who did it,
 * to which record, and the reason they gave. What the apps and the rest of the
 * system recorded is a different feed, on Event log.
 */

import { auditRecordsApiV1InternalAuditRecordsGet } from '@closet-theory/api';
import type { AuditRecordItem } from '@closet-theory/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@ui/components/base/buttons/button';
import { Callout } from '@ui/console/primitives';
import { RecordTable } from './record-table';
import { EntityRef, looksLikeUuid } from './references';
import { TextField } from '@ui/console/forms';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Actions, Page } from './components';

type Filters = { action: string; record: string };

/** The row as staff read it: plain column names, the detail kept for the drawer. */
function readable(item: AuditRecordItem) {
  return {
    id: item.id,
    when: item.occurred_at,
    what_was_done: item.action,
    done_by_role: item.actor_role ?? 'unknown',
    done_by: item.actor_id ?? 'system',
    record_type: item.resource_type,
    record: item.resource_id,
    reason_given: item.reason,
    outcome: item.outcome ?? 'not recorded',
    before: item.previous_value,
    after: item.new_value,
  };
}

export function AuditLog() {
  const [draft, setDraft] = useState<Filters>({ action: '', record: '' });
  const [filters, setFilters] = useState<Filters>({ action: '', record: '' });
  const [items, setItems] = useState<AuditRecordItem[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(
    async (after?: string) => {
      configureAdminApi();
      setLoading(true);
      setError(undefined);
      const result = await auditRecordsApiV1InternalAuditRecordsGet({
        query: {
          limit: 50,
          action: filters.action.trim() || null,
          resource_id: filters.record.trim() || null,
          cursor: after ?? null,
        },
      });
      setLoading(false);
      handleUnauthorized(result.response.status);
      if (result.response.status === 403) {
        setError('Your role cannot read the audit log.');
        return;
      }
      if (result.error || !result.data) {
        setError('The audit log could not be loaded. Anything already shown is still correct.');
        return;
      }
      const page = result.data;
      setItems((current) => (after ? [...current, ...page.items] : page.items));
      setCursor(page.next_cursor ?? undefined);
    },
    [filters],
  );
  useEffect(() => void load(), [load]);
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  return (
    <Page
      title="Audit log"
      purpose="Use this page to see what staff did and why: every change made on a member's behalf, every usage-limit change, operator control and account or access change, newest first, with the reason the person gave. Open a row to see the values before and after."
      toolbar={{
        onRefresh: () => void load(),
        filters: (
          <form
            className="flex flex-1 flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters(draft);
            }}
          >
            <TextField
              label="What was done"
              hint="The action name exactly as recorded, such as usage_limits.update."
              value={draft.action}
              onChange={(event) => setDraft({ ...draft, action: event.target.value })}
            />
            <TextField
              label="Record"
              hint="Paste a member or account id to see only what was done to it."
              value={draft.record}
              onChange={(event) => setDraft({ ...draft, record: event.target.value })}
            />
            <Button size="md" color="secondary" type="submit">
              Apply filters
            </Button>
          </form>
        ),
      }}
    >
      {error ? <Callout tone="error" title={error} /> : null}
      <RecordTable
        noun="staff actions"
        records={items.map(readable)}
        columns={[
          'when',
          'what_was_done',
          'done_by',
          'done_by_role',
          'record_type',
          'record',
          'reason_given',
          'outcome',
        ]}
        renderCell={(column, record) => {
          if (column !== 'done_by') return undefined;
          const actor = byId.get(String(record.id));
          const name = actor?.actor_name?.trim() || undefined;
          const email = actor?.actor_email?.trim() || undefined;
          if (looksLikeUuid(record.done_by))
            return (
              <EntityRef
                kind="staff"
                id={record.done_by}
                label={name ?? email}
                secondary={name ? email : undefined}
              />
            );
          return name ?? email;
        }}
        emptyHint="No staff action matches these filters. The read succeeded and came back empty — this is an answer, not a failure. Clear the filters to see everything."
      />
      {cursor ? (
        <Actions>
          <Button
            size="md"
            color="secondary"
            isDisabled={loading}
            onClick={() => void load(cursor)}
          >
            Load older actions
          </Button>
        </Actions>
      ) : null}
    </Page>
  );
}
